import '@mobiscroll/react/dist/css/mobiscroll.min.css'
import { type Carer, UserRole } from '@lmc/ts-common'
import {
  Eventcalendar,
  type MbscCalendarDayData,
  type MbscCalendarEventData,
  type MbscCellClickEvent,
  type MbscEventcalendarView,
  type MbscEventDragEvent,
  type MbscSelectedDateChangeEvent
} from '@mobiscroll/react'
import { message } from 'antd'
import { showToast } from 'common/utils'
import dayjs from 'dayjs'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'
import { DAILY, RESIDENT, VISIT, WEEKLY } from 'screens/Rostering/constants'
import {
  ALLOCATION_LIST_QUERY_KEY,
  INSTANCE_QUERY_KEY,
  INSTANCES_QUERY_KEY,
  REVISION_QUERY_KEY,
  REVISIONS_QUERY_KEY
} from 'screens/Rostering/hooks/queryConstants'
import useCheckGeneratedRota from 'screens/Rostering/hooks/useCheckGeneratedRota'
import type { CountChangesResponse } from 'screens/Rostering/hooks/useCountChanges'
import { useDrawerData } from 'screens/Rostering/hooks/useDrawerData'
import useRevisionRollback from 'screens/Rostering/hooks/useRevisionRollback'
import useRevisionUpdate, {
  type UpdateRevisionPayload
} from 'screens/Rostering/hooks/useRevisionUpdate'
import { RotaDrawer } from 'screens/Rostering/RotaDrawer'
import NewRotaPatternModal from 'screens/Rostering/RotaPattern/NewRotaPatternModal/NewRotaPatternModal'
import {
  type ResidentResource,
  type RotaBuilderFiltersType,
  RotaInstanceStatus,
  type RotaResidentEventInstance,
  type START_OVERLAY_TYPE
} from 'screens/Rostering/types'
import {
  BACKEND_REQUEST_STRING_FORMAT,
  getDateWithoutUTC,
  YEAR_FIRST_DATE_FORMAT
} from 'screens/Rostering/utils.datetime'
import { commonPatterns } from 'utils/enhancedTestIds'
import { RotaBuilderFilters } from '../../common'
import {
  RotaBuilderContentWrapper,
  RotaWrapper
} from '../../common/components/commonStyledComponents'
import Day from '../../common/components/eventcalendar/Day'
import Event from '../../common/components/eventcalendar/Event'
import EventCalendarHeader from '../../common/components/eventcalendar/EventcalendarHeader'
import Hour from '../../common/components/eventcalendar/Hour'
import Resource from '../../common/components/eventcalendar/Resource'
import { getWarningsListForEvent } from '../../common/components/eventcalendar/utils'
import StartRotaPublishModal from '../../common/components/modals/StartRotaPublishModal'
import PublishedRotaOverlay from '../../common/components/PublishedRotaOverlay'
import StartRotaOverlay from '../../common/components/StartRotaOverlay'
import UndoButton from '../../common/components/UndoButton'
import { CalendarOptions } from '../../common/constants'

type ResidentRotaProps = {
  testId?: string
  selectedDate: Date
  setSelectedDate: (date: Date) => void
  timelineView: MbscEventcalendarView['timeline']
  setTimelineView: (view: MbscEventcalendarView['timeline']) => void
  resources: ResidentResource[]
  events: RotaResidentEventInstance[]
  isLoading: boolean
  publishedRota?: boolean
  filterParams?: RotaBuilderFiltersType
  onFilterChange?: (value: RotaBuilderFiltersType) => void
  isDailyView: boolean
  refetchCountChanges?: () => void
  countChangesData: CountChangesResponse
  metadata: { unassignedHours: number }
  overlayType?: START_OVERLAY_TYPE
  hasFilters?: boolean
  calendarOptions?: typeof CalendarOptions
  showAllEvents?: boolean
  onToggleShowAllEvents?: () => void
}

export function ResidentRota({
  testId,
  selectedDate,
  setSelectedDate,
  timelineView,
  setTimelineView,
  resources,
  events,
  isLoading,
  publishedRota,
  filterParams,
  onFilterChange,
  isDailyView,
  refetchCountChanges,
  countChangesData,
  overlayType,
  hasFilters,
  calendarOptions,
  showAllEvents,
  onToggleShowAllEvents
}: ResidentRotaProps) {
  const baseId = testId || 'resident-rota'
  const { t } = useTranslation()

  const flags = useFeatureFlags({
    flagNames: [
      'rostering_parked_features',
      'new_create_pattern',
      'rostering_builder_drag_and_drop',
      'rota_warnings_email_notifications'
    ]
  })?.data

  const flagsRosteringParkedFeaturesEnabled =
    flags?.rostering_parked_features?.enabled

  const flagsRotaWarningsEmailNotifications =
    flags?.rota_warnings_email_notifications?.enabled

  const flagsNewCreatePatternEnabled = flags?.new_create_pattern?.enabled

  const userRole = useSelector(
    (state: { global: { user: Carer } }) => state.global.user?.role
  )
  const isAuditView = userRole === UserRole.auditView

  const flagsDragAndDropEnabled =
    flags?.rostering_builder_drag_and_drop?.enabled

  const [isZoomed, setIsZoomed] = useState(false)

  // Create view object with dynamic calendar options
  const view = useMemo(() => {
    const currentCalendarOptions = calendarOptions || CalendarOptions
    let dynamicTimelineView = timelineView

    // Override timelineView with our dynamic calendar options
    if (timelineView?.type === 'week') {
      dynamicTimelineView = currentCalendarOptions.weekly
    } else if (timelineView?.type === 'day') {
      if (isZoomed) {
        dynamicTimelineView = currentCalendarOptions.dailyZoomed
      } else {
        dynamicTimelineView = currentCalendarOptions.daily
      }
    }

    return { timeline: dynamicTimelineView }
  }, [timelineView, calendarOptions, isZoomed])

  const queryClient = useQueryClient()

  const {
    drawerVisible,
    setDrawerVisible,
    drawerAllocatedTeamVisible,
    setDrawerAllocatedTeamVisible,
    setDrawerDrawerOriginalDataId,
    drawerOriginalDataId
  } = useDrawerData()

  const { updateRevision } = useRevisionUpdate({
    selectedDate,
    intervalType: isDailyView ? DAILY : WEEKLY,
    onUpdateSuccess: () => {
      refetchCountChanges?.()
      queryClient?.invalidateQueries([INSTANCES_QUERY_KEY])
      queryClient?.invalidateQueries([INSTANCE_QUERY_KEY])
      queryClient?.invalidateQueries([REVISION_QUERY_KEY])
      queryClient?.invalidateQueries([REVISIONS_QUERY_KEY])
      queryClient?.invalidateQueries([ALLOCATION_LIST_QUERY_KEY])
    }
  })

  const handleUndoWithEventId = async (eventId: string) => {
    // Set the event ID and then use the existing rollback function
    setLastDraggedEventId(eventId)

    // Small delay to let the hook update
    setTimeout(async () => {
      if (!rollbackRevision) return

      try {
        // Hide all existing toasts first
        message.destroy()

        await rollbackRevision()

        setLastDraggedEventId(null)
        showToast({
          title: t('CHANGE_REVERTED_SUCCESSFULLY'),
          type: 'success'
        })
      } catch (error) {
        showToast({
          title: t('FAILED_TO_UNDO_CHANGE'),
          type: 'error'
        })
      }
    }, 100)
  }

  const onCalendarChange = useCallback(
    (event: { target: { value: string } }) => {
      const val = event.target.value
      if (val === 'weekly') {
        setTimelineView(CalendarOptions.weekly)
      } else if (val === DAILY) {
        setTimelineView(CalendarOptions.daily)
        if (dayjs(selectedDate).isSame(dayjs(), 'isoWeek')) {
          setSelectedDate(new Date())
        }
      }
    },
    [selectedDate, setSelectedDate, setTimelineView]
  )

  const handleZoom = useCallback(() => {
    setIsZoomed(!isZoomed)
    setTimelineView(
      isZoomed ? CalendarOptions.daily : CalendarOptions.dailyZoomed
    )
  }, [isZoomed, setTimelineView])

  const renderResource = useCallback(
    (resource: ResidentResource) => (
      <Resource resource={resource} type={RESIDENT} />
    ),
    []
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing dependency list; changing when this re-runs needs behavioural review
  const renderScheduleEvent = useCallback(
    (args: MbscCalendarEventData) => {
      return (
        <Event
          // @ts-ignore
          args={args}
          type={RESIDENT}
          instanceWarnings={countChangesData?.warnings?.instanceWarningMaps}
          warnings={getWarningsListForEvent(
            countChangesData?.warnings?.instanceWarningMaps ?? {},
            args?.original?.id as unknown as string,
            args?.original?.teamMembers,
            t,
            flagsRosteringParkedFeaturesEnabled,
            args?.original?.status,
            flagsRotaWarningsEmailNotifications
          )}
          setDrawerAllocatedTeamVisible={(flag) => {
            setDrawerDrawerOriginalDataId(args?.id)
            setDrawerAllocatedTeamVisible(flag)
          }}
          setDrawerVisible={(flag) => {
            setDrawerDrawerOriginalDataId(args?.id)
            setDrawerVisible(flag)
          }}
          isDailyView={isDailyView}
          publishedRota={publishedRota}
          testId={`${baseId}-event-${args?.original?.id}`}
        />
      )
    },
    [
      countChangesData?.warnings,
      events,
      isDailyView,
      publishedRota,
      baseId,
      flagsRosteringParkedFeaturesEnabled,
      flagsRotaWarningsEmailNotifications
    ]
  )

  const renderHour = (args: MbscCalendarDayData) => {
    return <Hour date={args.date} />
  }

  const renderDay = (args: MbscCalendarDayData) => {
    return <Day date={args.date} isDailyView={isDailyView} />
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing dependency list; changing when this re-runs needs behavioural review
  const renderHeader = useCallback(() => {
    return (
      <EventCalendarHeader
        selectedDate={selectedDate}
        onCalendarChange={onCalendarChange}
        handleZoom={handleZoom}
        isZoomed={isZoomed}
        isDailyView={isDailyView}
        countChangesData={countChangesData}
        publishedRota={publishedRota}
        filterParams={filterParams}
        showAllEvents={showAllEvents}
        onToggleShowAllEvents={onToggleShowAllEvents}
      />
    )
  }, [
    selectedDate,
    onCalendarChange,
    handleZoom,
    timelineView,
    isZoomed,
    isDailyView,
    countChangesData,
    publishedRota,
    filterParams
  ])

  const [originalEventState, setOriginalEventState] = useState<{
    start: string | Date
    end: string | Date
  } | null>(null)

  // State to track last dragged event for undo functionality
  const [lastDraggedEventId, setLastDraggedEventId] = useState<string | null>(
    null
  )

  const { rollbackRevision } = useRevisionRollback({
    id: lastDraggedEventId || undefined
  })

  const handleDragStart = (e: MbscEventDragEvent) => {
    // Store original state before drag
    setOriginalEventState({
      start: e.event.start as string | Date,
      end: e.event.end as string | Date
    })
  }

  const handleDragEvent = async (e: MbscEventDragEvent): Promise<boolean> => {
    // Check for restricted event statuses (matching drawer restrictions)
    const isInProgress =
      e.event.status === RotaInstanceStatus.IN_PROGRESS ||
      e.event.clockIns?.length > 0
    const isDone = e.event.status === RotaInstanceStatus.DONE
    const isOverdue = e.event.status === RotaInstanceStatus.OVERDUE
    const isCancelled = e.event.status === RotaInstanceStatus.CANCELLED

    const isInvalidStatusDrop =
      isInProgress || isDone || isOverdue || isCancelled

    if (isInvalidStatusDrop) {
      let errorMessage = t('CANNOT_EDIT_ALLOCATION_STATUS')

      if (isInProgress) {
        errorMessage = t('CANNOT_EDIT_IN_PROGRESS_EVENT')
      } else if (isOverdue) {
        errorMessage = t('CANNOT_EDIT_OVERDUE_EVENT')
      }

      showToast({
        title: errorMessage,
        type: 'error'
      })

      // Revert event to original position
      if (originalEventState) {
        e.event.start = originalEventState.start
        e.event.end = originalEventState.end
      }
      setOriginalEventState(null)
      return false
    }

    // Store original state for undo (from drag start)
    let undoData: {
      eventId: string
      originalStart: string | Date
      originalEnd: string | Date
    } | null = null
    if (originalEventState) {
      undoData = {
        eventId: e?.event?.id?.toString() || '',
        originalStart: originalEventState.start,
        originalEnd: originalEventState.end
      }
    }

    try {
      const updatePayload: UpdateRevisionPayload = {
        eventId: e?.event?.id?.toString() || '',
        startDate: getDateWithoutUTC(
          e?.event?.start as string | Date,
          BACKEND_REQUEST_STRING_FORMAT
        ),
        endDate: getDateWithoutUTC(
          e?.event?.end as string | Date,
          BACKEND_REQUEST_STRING_FORMAT
        )
      }

      // Add undo action for drag operations (only when feature flag is enabled)
      if (originalEventState && flagsDragAndDropEnabled) {
        const eventId = e?.event?.id?.toString() || ''
        setLastDraggedEventId(eventId)

        updatePayload.undoAction = (
          <UndoButton onClick={() => handleUndoWithEventId(eventId)} />
        )
      }

      await updateRevision?.(updatePayload)
      setOriginalEventState(null) // Clear original state
      return true
    } catch (error) {
      showToast({
        title: t('ERROR_UPDATING_EVENT'),
        type: 'error'
      })
      setOriginalEventState(null) // Clear original state
      return false
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing dependency list; changing when this re-runs needs behavioural review
  const handleSelectedDateChange = useCallback(
    (args: MbscSelectedDateChangeEvent) => {
      setSelectedDate(args.date as Date)
    },
    []
  )

  const { data: hasRotaData, isFetched: isRotaFetched } = useCheckGeneratedRota(
    { selectedDate }
  )

  const [isInitialized, setIsInitialized] = useState(false)
  const [hasEvents, setHasEvents] = useState(false)

  const [showStartRotaModal, setShowStartRotaModal] = useState(false)

  const openStartRotaModal = () => {
    setShowStartRotaModal(true)
  }

  const closeStartRotaModal = () => {
    setShowStartRotaModal(false)
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (isRotaFetched) {
      setHasEvents(events.length > 0 || !!hasRotaData)
      setIsInitialized(true)
    }
  }, [selectedDate, hasRotaData, isRotaFetched, events])

  const [clickedDate, setClickedDate] = useState<Date | null>(null)
  const [clickedResidentId, setClickedResidentId] = useState<string | null>(
    null
  )
  const [clickedTimeStart, setClickedTimeStart] = useState<string | null>(null)
  const [clickedTimeEnd, setClickedTimeEnd] = useState<string | null>(null)
  const [showPatternModal, setShowPatternModal] = useState(false)

  const handleCellClick = (event: MbscCellClickEvent) => {
    const date = new Date(event.date)
    setClickedDate(date)

    if (event.resource) {
      setClickedResidentId(String(event.resource))

      if (isDailyView) {
        const clickedHour = date.getHours()
        const clickedMinute = date.getMinutes()

        const startTime = `${String(clickedHour).padStart(2, '0')}:${String(clickedMinute).padStart(2, '0')}`

        const endHour = (clickedHour + 1) % 24
        const endTime = `${String(endHour).padStart(2, '0')}:${String(clickedMinute).padStart(2, '0')}`

        setClickedTimeStart(startTime)
        setClickedTimeEnd(endTime)
      } else {
        setClickedTimeStart(null)
        setClickedTimeEnd(null)
      }

      setShowPatternModal(true)
    }
  }

  const getInitialValues = () => {
    const values: {
      reoccurenceTooglePattern: boolean
      patternStart?: ReturnType<typeof dayjs>
      subjectId?: string
      startTime?: string
      endTime?: string
    } = {
      reoccurenceTooglePattern: false
    }

    if (clickedDate) {
      values.patternStart = dayjs(clickedDate)
    }

    if (clickedResidentId) {
      values.subjectId = clickedResidentId
    }

    if (isDailyView && clickedTimeStart && clickedTimeEnd) {
      values.startTime = clickedTimeStart
      values.endTime = clickedTimeEnd
    }

    return values
  }

  return (
    <RotaWrapper data-testid={baseId}>
      <RotaBuilderFilters
        testId={`${baseId}-filters`}
        builderType={RESIDENT}
        value={filterParams}
        onChange={onFilterChange}
        publishedRota={publishedRota}
      />
      <RotaBuilderContentWrapper data-testid={`${baseId}-content`}>
        <Eventcalendar
          data-testid={`${baseId}-calendar`}
          firstDay={1}
          timeFormat="HH:mm"
          className="eventcalendar"
          dragToMove={
            !publishedRota &&
            isDailyView &&
            flagsDragAndDropEnabled &&
            !isAuditView
          }
          eventDelete={!publishedRota && !isAuditView}
          dragInTime={
            !publishedRota &&
            isDailyView &&
            flagsDragAndDropEnabled &&
            !isAuditView
          }
          dragToResize={
            !publishedRota &&
            isDailyView &&
            flagsDragAndDropEnabled &&
            !isAuditView
          }
          showEventTooltip={false}
          dragBetweenResources={false}
          clickToCreate={false}
          dragToCreate={false}
          view={view}
          data={events}
          resources={resources}
          renderDay={renderDay}
          renderHour={renderHour}
          renderEvent={renderScheduleEvent}
          renderScheduleEvent={renderScheduleEvent}
          renderResource={renderResource}
          renderHeader={renderHeader}
          onEventDragStart={handleDragStart}
          onEventDragEnd={handleDragEvent}
          selectedDate={selectedDate}
          onSelectedDateChange={handleSelectedDateChange}
          onCellClick={
            flagsNewCreatePatternEnabled && !publishedRota && !isAuditView
              ? handleCellClick
              : undefined
          }
        />
        {!publishedRota && isInitialized && (
          <StartRotaOverlay
            data-testid={`${baseId}-start-overlay`}
            hasEvents={hasEvents}
            isLoading={isLoading}
            onStartRota={openStartRotaModal}
            type={overlayType}
            isAuditView={isAuditView}
          />
        )}
        {publishedRota && events.length === 0 && hasFilters && (
          <PublishedRotaOverlay
            data-testid={`${baseId}-published-overlay`}
            isLoading={isLoading}
          />
        )}
      </RotaBuilderContentWrapper>
      {showPatternModal && (
        <NewRotaPatternModal
          testId={commonPatterns.modal(baseId, 'new-pattern')}
          onCancel={() => setShowPatternModal(false)}
          type={VISIT}
          initialValues={getInitialValues()}
        />
      )}
      {showStartRotaModal && (
        <StartRotaPublishModal
          data-testid={commonPatterns.modal(baseId, 'start-rota')}
          open={showStartRotaModal}
          onClose={closeStartRotaModal}
          fromDate={dayjs(selectedDate)
            .startOf('isoWeek')
            .format(YEAR_FIRST_DATE_FORMAT)}
          toDate={dayjs(selectedDate)
            .endOf('isoWeek')
            .format(YEAR_FIRST_DATE_FORMAT)}
        />
      )}
      {drawerVisible && (
        <RotaDrawer
          data-testid={`${baseId}-drawer`}
          onClose={() => {
            setDrawerVisible(false)
            setDrawerDrawerOriginalDataId(undefined)
          }}
          isVisible={drawerVisible}
          id={drawerOriginalDataId}
          publishedRota={publishedRota}
          instanceWarningMaps={countChangesData?.warnings?.instanceWarningMaps}
        />
      )}
      {drawerAllocatedTeamVisible && (
        <RotaDrawer
          data-testid={`${baseId}-allocated-team-drawer`}
          onClose={() => {
            setDrawerAllocatedTeamVisible(false)
            setDrawerDrawerOriginalDataId(undefined)
          }}
          isVisible={drawerAllocatedTeamVisible}
          id={drawerOriginalDataId}
          defaultTab={2}
          publishedRota={publishedRota}
          instanceWarningMaps={countChangesData?.warnings?.instanceWarningMaps}
        />
      )}
    </RotaWrapper>
  )
}

export default ResidentRota
